import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from 'react-hot-toast';
import { TourProvider } from "./contexts/TourContext";
import { ShowFinderProvider } from "./contexts/ShowFinderContext";
import { StreamingServicesProvider } from "./contexts/StreamingServicesContext";
import ProductTour from "./components/ProductTour";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import MyShows from "./pages/MyShows";
import SocialFeed from "./pages/SocialFeed";
import Friends from "./pages/Friends";
import FreshPastures from "./pages/FreshPastures";
import Security from "./pages/Security";
import Help from "./pages/Help";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import AdminEmail from "./pages/AdminEmail";
import About from "./pages/About";
import FAQPage from "./pages/FAQPage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Trending from "./pages/Trending";
import PublicShow from "./pages/PublicShow";
import InstallPrompt from "./components/InstallPrompt";
import EmailPreferencesModal from "./components/EmailPreferencesModal";
import { initializeNativeFeatures, initializePushNotifications, addPushNotificationListeners, isNativePlatform } from "./utils/nativeFeatures";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Add axios interceptor to include backup token for iOS Safari
// This ensures ALL API calls use the Bearer token if available
axios.interceptors.request.use((config) => {
  // Skip if Authorization header is already set explicitly
  if (config.headers.Authorization) {
    return config;
  }
  
  // Add Bearer token from localStorage if available
  const backupToken = localStorage.getItem('backup_session_token');
  if (backupToken) {
    config.headers.Authorization = `Bearer ${backupToken}`;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Auth wrapper component
const AuthWrapper = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showEmailPrefs, setShowEmailPrefs] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const usernamePromptShown = useRef(false);
  const emailPrefsChecked = useRef(false);
  const authProcessed = useRef(false);  // Prevent double auth processing

  // Initialize native features (Capacitor) on app start
  useEffect(() => {
    const initNative = async () => {
      try {
        if (isNativePlatform()) {
          console.log('Initializing native features...');
          await initializeNativeFeatures();
          
          // Initialize push notifications
          const pushEnabled = await initializePushNotifications();
          if (pushEnabled) {
            // Set up push notification listeners
            const cleanup = await addPushNotificationListeners({
              onRegistration: (token) => {
                console.log('Push token:', token);
                // TODO: Send token to backend for storing
              },
              onPushReceived: (notification) => {
                // Show toast for foreground notifications
                toast(notification.title || 'New notification', {
                  icon: '🐑',
                  duration: 4000
                });
              },
              onPushActionPerformed: (action) => {
                // Handle notification tap - navigate to relevant page
                const data = action.notification.data;
                if (data?.type === 'new_episode') {
                  // Could navigate to the show
                  console.log('User tapped episode notification');
                }
              }
            });
            
            // Store cleanup function for later
            window.pushNotificationCleanup = cleanup;
          }
        }
      } catch (error) {
        console.log('Native features not available:', error);
      }
    };
    
    initNative();
    
    return () => {
      // Cleanup push notification listeners on unmount
      if (window.pushNotificationCleanup) {
        window.pushNotificationCleanup();
      }
    };
  }, []);

  useEffect(() => {
    // Update theme-color meta tag dynamically to override any cached values
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", "#E62F88");
    }

    const processAuth = async () => {
      // Prevent double-processing (critical for iOS)
      if (authProcessed.current) {
        console.log('Auth already processed, skipping');
        return;
      }
      
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      console.log('Processing auth, hash:', hash);
      
      if (hash && hash.includes('session_id=')) {
        // Mark as processed IMMEDIATELY to prevent race conditions
        authProcessed.current = true;
        
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        console.log('Session ID found:', sessionId);
        
        // Clean URL immediately to prevent double-processing
        window.history.replaceState({}, document.title, window.location.pathname);
        
        try {
          console.log('Posting session to backend...');
          const sessionResponse = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
            withCredentials: true
          });
          console.log('Session posted successfully:', sessionResponse.data);
          
          // Get the session token from response
          const sessionToken = sessionResponse.data.session_token;
          
          if (!sessionToken) {
            throw new Error('No session token received from server');
          }
          
          // Store session token in localStorage IMMEDIATELY
          localStorage.setItem('backup_session_token', sessionToken);
          console.log('Session token stored in localStorage:', sessionToken.substring(0, 10) + '...');
          
          // ALWAYS use Bearer token directly for the auth check (most reliable on all platforms)
          console.log('Fetching user data with Bearer token...');
          const response = await axios.get(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          });
          
          console.log('Auth successful, user:', response.data.email);
          setUser(response.data);
          setLoading(false);
          navigate('/dashboard');
          
        } catch (error) {
          console.error('Auth error:', error);
          console.error('Error details:', error.response?.data);
          // Clear any stale token on error
          localStorage.removeItem('backup_session_token');
          setUser(null);
          setLoading(false);
          authProcessed.current = false;  // Allow retry
        }
      } else {
        // No hash - check existing session
        console.log('Checking existing session...');
        
        // Check localStorage token first (most reliable for iOS)
        const storedToken = localStorage.getItem('backup_session_token');
        
        if (storedToken) {
          try {
            console.log('Found stored token, validating...');
            const response = await axios.get(`${API}/auth/me`, {
              headers: { 'Authorization': `Bearer ${storedToken}` }
            });
            console.log('Session restored from localStorage');
            setUser(response.data);
            setLoading(false);
            return;
          } catch (tokenError) {
            console.log('Stored token invalid, clearing...');
            localStorage.removeItem('backup_session_token');
          }
        }
        
        // Try cookie-based auth as fallback (works on desktop)
        try {
          const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
          console.log('Existing session found via cookie');
          // Also save to localStorage for future use
          setUser(response.data);
          setLoading(false);
        } catch (error) {
          console.log('No valid session found');
          setUser(null);
          setLoading(false);
        }
      }
    };

    processAuth();
  }, [navigate]);

  // Check for username after user is loaded
  useEffect(() => {
    if (user && !user.username && location.pathname !== '/profile' && !usernamePromptShown.current) {
      // Mark as shown to prevent duplicates
      usernamePromptShown.current = true;
      
      // Show notification to set username
      toast((t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ fontWeight: 600, color: '#ffffff' }}>
            👋 Welcome to Baadtv!
          </div>
          <div style={{ fontSize: '0.9rem', color: '#e0e0e0' }}>
            Please set a unique username so friends can find you in search.
          </div>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              navigate('/profile');
            }}
            style={{
              background: '#E62F88',
              color: '#000000',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: '0.25rem'
            }}
          >
            Set Username Now
          </button>
        </div>
      ), {
        duration: 8000,
        position: 'top-center',
        style: {
          background: '#1a1a1a',
          color: '#ffffff',
          border: '1px solid #E62F88',
          padding: '1rem',
          maxWidth: '400px'
        }
      });
    }
  }, [user, location.pathname, navigate]);

  // Check for email preferences setup after user is loaded AND has a username
  useEffect(() => {
    const checkEmailPrefs = async () => {
      // Only check if user exists, has a username, and we haven't checked yet
      // This ensures the email modal appears AFTER the username setup is complete
      if (user && user.username && !emailPrefsChecked.current) {
        emailPrefsChecked.current = true;
        
        try {
          const response = await axios.get(`${API}/email/preferences/needs-setup`, {
            withCredentials: true
          });
          
          if (response.data.needs_setup) {
            // Small delay to let the page settle first
            setTimeout(() => {
              setShowEmailPrefs(true);
            }, 1500);
          }
        } catch (error) {
          console.log('Could not check email preferences:', error);
        }
      }
    };
    
    checkEmailPrefs();
  }, [user]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      {children({ user, setUser })}
      <EmailPreferencesModal 
        isOpen={showEmailPrefs} 
        onClose={() => setShowEmailPrefs(false)}
        userName={user?.name?.split(' ')[0]}
      />
    </>
  );
};

// Protected route component
const ProtectedRoute = ({ children, user }) => {
  if (!user) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1a1a1a',
            color: '#ffffff',
            border: '1px solid #E62F88',
          },
          success: {
            iconTheme: {
              primary: '#E62F88',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ff4444',
              secondary: '#ffffff',
            },
          },
        }}
      />
      <BrowserRouter>
          <AuthWrapper>
            {({ user, setUser }) => (
              <TourProvider user={user}>
                <ShowFinderProvider user={user} setUser={setUser}>
                <StreamingServicesProvider user={user}>
                <ProductTour />
                <Routes>
              <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute user={user}>
                    <Dashboard user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-shows"
                element={
                  <ProtectedRoute user={user}>
                    <MyShows user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feed"
                element={
                  <ProtectedRoute user={user}>
                    <SocialFeed user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/friends"
                element={
                  <ProtectedRoute user={user}>
                    <Friends user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fresh-pastures"
                element={
                  <ProtectedRoute user={user}>
                    <FreshPastures user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <ProtectedRoute user={user}>
                    <Help user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute user={user}>
                    <Profile user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/security"
                element={
                  <ProtectedRoute user={user}>
                    <Security user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute user={user}>
                    <Admin user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/email"
                element={
                  <ProtectedRoute user={user}>
                    <AdminEmail user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <ProtectedRoute user={user}>
                    <About user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/trending" element={<Trending user={user} />} />
              <Route path="/trending/:region" element={<Trending user={user} />} />
              <Route path="/show/:tmdbId" element={<PublicShow user={user} />} />
              <Route path="/blog" element={
                <AuthWrapper>
                  {(user, loading) => (
                    <Blog user={user} setUser={setUser} />
                  )}
                </AuthWrapper>
              } />
              <Route path="/blog/:slug" element={
                <AuthWrapper>
                  {(user, loading) => (
                    <BlogPost user={user} setUser={setUser} />
                  )}
                </AuthWrapper>
              } />
                </Routes>
                </StreamingServicesProvider>
                </ShowFinderProvider>
              </TourProvider>
            )}
          </AuthWrapper>
          <InstallPrompt />
      </BrowserRouter>
    </div>
  );
}

export default App;